import React, { useState, Component } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { MailIcon, ClockIcon, MapPinIcon } from 'lucide-react';
import { contactFormSchema, ContactFormData } from '../lib/validation';
import { Input } from '../components/ui/Input';
import { Textarea } from '../components/ui/Textarea';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { useTranslation } from '../lib/i18n';
export function ContactPage() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const {
    lang
  } = useTranslation();
  const en = lang === 'en';
  const {
    register,
    handleSubmit,
    formState: {
      errors
    }
  } = useForm<ContactFormData>({
    resolver: zodResolver(contactFormSchema)
  });
  const onSubmit = async (data: ContactFormData) => {
    setIsSubmitting(true);
    const emailBody = `D-TECH Solutions Contact\n\nName: ${data.name}\nEmail: ${data.email}\nPhone: ${data.phone}\n\nMessage:\n${data.message}`;
    window.location.href = `mailto:info@dtech-solutions.com?subject=Contact form&body=${encodeURIComponent(emailBody)}`;
    setTimeout(() => {
      setIsSubmitting(false);
      setSubmitSuccess(true);
    }, 1000);
  };
  return <main className="min-h-screen bg-dark-bg" data-id="element-580">
      <section className="py-20 bg-dark-surface border-b-2 border-dark-border" data-id="element-581">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-582">
          <h1 className="text-5xl md:text-6xl font-black text-white mb-6" data-id="element-583">
            <span className="text-brand-yellow" data-id="element-584">
              {en ? 'Contact' : 'Kontaktirajte'}
            </span>{' '}
            {en ? 'us' : 'nas'}
          </h1>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto" data-id="element-585">
            {en ? 'Get in touch with our team for product inquiries, technical support or partnership opportunities.' : 'Stupite u kontakt sa našim timom za upite o proizvodima, tehničku podršku ili mogućnosti partnerstva.'}
          </p>
        </div>
      </section>

      <section className="py-20" data-id="element-586">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-587">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12" data-id="element-588">
            <div data-id="element-589">
              <h2 className="text-3xl font-black text-white mb-6" data-id="element-590">
                {en ? 'Send us a message' : 'Pošaljite nam poruku'}
              </h2>
              {submitSuccess ? <Card data-id="element-591">
                  <div className="text-center py-8" data-id="element-592">
                    <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-593">
                      <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-594">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" data-id="element-595" />
                      </svg>
                    </div>
                    <h3 className="text-2xl font-bold text-white mb-2" data-id="element-596">
                      {en ? 'Message sent!' : 'Poruka poslata!'}
                    </h3>
                    <p className="text-gray-300" data-id="element-597">
                      {en ? 'Thank you for contacting us. We will get back to you shortly.' : 'Hvala vam što ste nas kontaktirali. Uskoro ćemo vam se javiti.'}
                    </p>
                  </div>
                </Card> : <Card data-id="element-598">
                  <form onSubmit={handleSubmit(onSubmit)} className="space-y-6" data-id="element-599">
                    <Input label={en ? 'Full name *' : 'Ime i prezime *'} type="text" {...register('name')} error={errors.name?.message} placeholder={en ? 'Your name' : 'Vaše ime i prezime'} data-id="element-600" />
                    <Input label={en ? 'Email *' : 'Email *'} type="email" {...register('email')} error={errors.email?.message} placeholder={en ? 'your@email.com' : 'vas.email@primer.com'} data-id="element-601" />
                    <Input label={en ? 'Phone *' : 'Telefon *'} type="tel" {...register('phone')} error={errors.phone?.message} placeholder={en ? '+1 ...' : '+381 60 123 4567'} data-id="element-602" />
                    <Textarea label={en ? 'Message *' : 'Poruka *'} {...register('message')} error={errors.message?.message} placeholder={en ? 'Tell us about your needs or questions' : 'Recite nam o vašim potrebama ili pitanjima'} rows={6} data-id="element-603" />
                    <Button type="submit" variant="primary" size="lg" disabled={isSubmitting} className="w-full" data-id="element-604">
                      {isSubmitting ? en ? 'Sending...' : 'Šaljem...' : en ? 'Send message' : 'Pošalji poruku'}
                    </Button>
                  </form>
                </Card>}
            </div>

            <div data-id="element-605">
              <h2 className="text-3xl font-black text-white mb-6" data-id="element-606">
                {en ? 'Contact information' : 'Kontakt informacije'}
              </h2>
              <div className="space-y-6" data-id="element-607">
                <Card data-id="element-608">
                  <div className="flex items-start space-x-4" data-id="element-609">
                    <div className="w-12 h-12 bg-brand-yellow rounded-industrial flex items-center justify-center flex-shrink-0" data-id="element-610">
                      <MailIcon className="w-6 h-6 text-dark-bg" data-id="element-611" />
                    </div>
                    <div data-id="element-612">
                      <h3 className="text-xl font-bold text-white mb-2" data-id="element-613">
                        Email
                      </h3>
                      <a href="mailto:info@dtech-solutions.com" className="text-brand-yellow hover:text-brand-yellow-dark transition-colors text-lg" data-id="element-614">
                        info@dtech-solutions.com
                      </a>
                      <p className="text-sm text-gray-400 mt-2" data-id="element-615">
                        {en ? 'For all inquiries, quotes and support' : 'Za sve upite, ponude i podršku'}
                      </p>
                    </div>
                  </div>
                </Card>
                <Card data-id="element-616">
                  <div className="flex items-start space-x-4" data-id="element-617">
                    <div className="w-12 h-12 bg-brand-yellow rounded-industrial flex items-center justify-center flex-shrink-0" data-id="element-618">
                      <ClockIcon className="w-6 h-6 text-dark-bg" data-id="element-619" />
                    </div>
                    <div data-id="element-620">
                      <h3 className="text-xl font-bold text-white mb-2" data-id="element-621">
                        {en ? 'Working hours' : 'Radno vreme'}
                      </h3>
                      <p className="text-gray-300 text-lg" data-id="element-622">
                        {en ? <>
                            Monday - Friday
                            <br data-id="element-623" />
                            10:00 - 17:00 (CET)
                          </> : <>
                            Ponedeljak - Petak
                            <br data-id="element-624" />
                            10:00 - 17:00 (CET)
                          </>}
                      </p>
                      <p className="text-sm text-gray-400 mt-2" data-id="element-625">
                        {en ? 'We respond to all inquiries within 24 business hours' : 'Odgovaramo na sve upite u roku od 24 radna sata'}
                      </p>
                    </div>
                  </div>
                </Card>
                <Card data-id="element-626">
                  <div className="flex items-start space-x-4" data-id="element-627">
                    <div className="w-12 h-12 bg-brand-yellow rounded-industrial flex items-center justify-center flex-shrink-0" data-id="element-628">
                      <MapPinIcon className="w-6 h-6 text-dark-bg" data-id="element-629" />
                    </div>
                    <div data-id="element-630">
                      <h3 className="text-xl font-bold text-white mb-2" data-id="element-631">
                        {en ? 'Location' : 'Lokacija'}
                      </h3>
                      <p className="text-gray-300" data-id="element-632">
                        {en ? <>
                            Online-first business
                            <br data-id="element-633" />
                            Physical location in preparation
                          </> : <>
                            Online-first poslovanje
                            <br data-id="element-634" />
                            Fizička lokacija u pripremi
                          </>}
                      </p>
                    </div>
                  </div>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>;
}